// src/components/ScreenshotFlow.js

import React, { useState, useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import "./ScreenshotFlow.css";

export default function ScreenshotFlow({ apiBaseUrl }) {
  const API_BASE = (apiBaseUrl || process.env.REACT_APP_API_BASE_URL || "").replace(
    /\/+$/,
    ""
  );

  const { getIdTokenClaims } = useAuth0();

  const [files, setFiles] = useState([]);
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [sopResult, setSopResult] = useState(null);

  const authTokenRef = useRef("");
  const pollRef = useRef(null);

  const handleFilesChange = (e) => {
    setError("");

    const incoming = Array.from(e.target.files || []);
    if (!incoming.length) return;

    setFiles((prev) => {
      const existing = Array.isArray(prev) ? prev : [];
      const merged = [...existing];

      incoming.forEach((file) => {
        const duplicate = merged.find(
          (f) =>
            f.name === file.name &&
            f.size === file.size &&
            f.lastModified === file.lastModified
        );
        if (!duplicate) {
          merged.push(file);
        }
      });

      return merged;
    });

    setTaskId(null);
    setSopResult(null);
    e.target.value = "";
  };

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setTaskId(null);
    setSopResult(null);
    setError("");
  };

  const handleSubmit = async () => {
    setError("");
    setTaskId(null);
    setSopResult(null);

    if (!files.length) {
      setError("Please select at least one screenshot.");
      return;
    }

    setLoading(true);

    let bearer = "";
    let userEmail = "";
    try {
      const claims = await getIdTokenClaims();
      bearer = claims.__raw;
      authTokenRef.current = bearer;
      userEmail = claims.email || "";
      if (!userEmail) {
        throw new Error("Missing email claim in token");
      }
    } catch (e) {
      console.error("Auth error / missing email:", e);
      setError("Authentication error – please log in again.");
      setLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("batchName", "screenshot-flow");

      const metas = files.map((f, i) => ({
        orderIndex: i,
        title: f.name,
        url: "",
      }));

      formData.append("metas", JSON.stringify(metas));
      files.forEach((f) => formData.append("files", f));

      const uploadResp = await axios.post(
        `${API_BASE}/sopscreenshots/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${bearer}`,
          },
        }
      );

      const batchId = uploadResp.data.batchId;

      const agentResp = await axios.post(
        `${API_BASE}/sopscreenshots/process/${batchId}`,
        {
          notes,
          user_email: userEmail,
        },
        {
          headers: {
            Authorization: `Bearer ${bearer}`,
            "Content-Type": "application/json",
          },
        }
      );

      setTaskId(agentResp.data.task_id);
    } catch (err) {
      console.error("ScreenshotFlow error:", err);
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Failed to start SOP generation."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!taskId) return;

    pollRef.current = setInterval(async () => {
      try {
        const statusResp = await axios.get(`${API_BASE}/tasks/${taskId}`, {
          headers: { Authorization: `Bearer ${authTokenRef.current}` },
        });

        const { status, result } = statusResp.data;
        if (status !== "SUCCESS" || !result) return;

        const { docx_url, pdf_url } = result;
        setSopResult({ docx_url, pdf_url });
        clearInterval(pollRef.current);
      } catch (err) {
        console.error("Polling error:", err);
        setError("Error checking SOP status: " + err.message);
        clearInterval(pollRef.current);
      }
    }, 2000);

    return () => clearInterval(pollRef.current);
  }, [taskId, API_BASE]);

  return (
    <div className="screenshot-flow">
      <div className="sf-form">
        <div className="sf-field">
          <label>Select Screenshots</label>
          <input
            type="file"
            accept="image/*,.pdf"
            multiple
            onChange={handleFilesChange}
            disabled={loading}
          />
        </div>

        {files.length > 0 && (
          <ul className="sf-file-list">
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`}>
                <span>📄 {f.name}</span>
                <button
                  type="button"
                  className="sf-file-remove"
                  onClick={() => handleRemoveFile(i)}
                  disabled={loading}
                  aria-label={`Remove ${f.name}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="sf-field">
          <label>Notes (optional)</label>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={loading}
            placeholder="Any additional context…"
          />
        </div>

        {error && <div className="sf-error">{error}</div>}

        <button className="sf-button" onClick={handleSubmit} disabled={loading}>
          {loading ? "Generating…" : "Generate SOP"}
        </button>
      </div>

      {taskId && !sopResult && !error && (
        <div className="status status--active">
          Generating your SOP… please wait.
        </div>
      )}

      {sopResult && (
        <div className="result">
          <div className="status status--success">✅ Your SOP draft is ready!</div>
          <div className="download-links">
            <a
              href={sopResult.pdf_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              📄 Download PDF
            </a>
            <a
              href={sopResult.docx_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              📝 Download Word
            </a>
          </div>
        </div>
      )}
    </div>
  );
}